import type { RefObject } from "react";
import { useRef } from "react";
import { useLocation } from "react-router-dom";
import { Flex, Text } from "@theorgchartdev/storybook";
import type { ListItemOption, OpenCtrlRef } from "@theorgchartdev/storybook";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { useViewManagerContext } from "@/providers/ViewManagerProvider";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import * as StateLib from "@/Utils/StateLib";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appstr } from "@coreapp/utils/appstr";
import { enumFeaturesList } from "@coreapp/constants";
import UpgradePopover, {
  type PopoverText
} from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradePopover";
import { getPlanningUpgradePopoverText, type FeatureKey } from "./useMainNavItems";

type SettingsListItem = {
  label: string;
  value: string;
  divider?: boolean;
  defaultSection?: string;
  showUpgrade?: boolean;
  featureKey?: FeatureKey;
  upgradePopoverText?: PopoverText;
};

export type SettingMenuOption = ListItemOption & { defaultSection?: string };

export type UseSettingsMenuOptionsResult = {
  settingOptions: SettingMenuOption[];
  canAccessSettings: boolean;
  isWithinSettings: boolean;
  menuOpenCtrlRef: RefObject<OpenCtrlRef | null>;
};

export const useSettingsMenuOptions = (): UseSettingsMenuOptionsResult => {
  const {
    canAccessViewLibrary,
    canAccessTemplates,
    canAccessChartSettings,
    canAccessAbout,
    showPricingAndPackaging,
    shouldShowPlanSettings,
    shouldShowUpgradePlanning
  } = usePermissionCheck();
  const { setShowViewLibrary } = useViewManagerContext();
  const nav = useAppNavigate();
  const location = useLocation();
  const menuOpenCtrlRef = useRef<OpenCtrlRef | null>(null);
  const wsMgr = StateLib.GetWSMgr();
  const doc = StateLib.GetDoc();
  const canAccessSettings = !!Globals._uiConfig?.fHasSettings;

  const onClickSettings = (option: SettingsListItem) => {
    switch (option.value) {
      case "employee-view-library":
      case "position-view-library":
        setShowViewLibrary(true);
        break;
      case "chart-settings":
        nav.toChartSettings();
        wsMgr?.UpdateDocTitle();
        break;
      case "account-settings":
        nav.toAccountSettings();
        wsMgr?.UpdateDocTitle();
        break;
      case "plan-settings":
        nav.toPlanSettings();
        wsMgr?.UpdateDocTitle();
        break;
      case "details-account":
        nav.toDetails("account");
        wsMgr?.UpdateDocTitle();
        break;
      case "details-application":
        nav.toDetails("application");
        wsMgr?.UpdateDocTitle();
        break;
    }
  };

  const settingsList: SettingsListItem[] = [
    ...(canAccessChartSettings
      ? [
          {
            label: "Chart Settings",
            value: "chart-settings"
          },
          {
            label: "Account Settings",
            value: "account-settings"
          }
        ]
      : []),
    ...(shouldShowPlanSettings
      ? [
          {
            label: "Plan settings",
            value: "plan-settings",
            showUpgrade: shouldShowUpgradePlanning,
            featureKey: enumFeaturesList.planning,
            upgradePopoverText: getPlanningUpgradePopoverText()
          }
        ]
      : []),
    ...(canAccessTemplates && (canAccessViewLibrary || showPricingAndPackaging)
      ? [
          {
            label: doc?.IsPositionDocument() ? "Position view library" : "View library",
            value: doc?.IsPositionDocument() ? "position-view-library" : "employee-view-library",
            divider: true,
            showUpgrade: !canAccessViewLibrary && showPricingAndPackaging,
            featureKey: enumFeaturesList.promoteViews,
            upgradePopoverText: {
              message: appstr("view library upgrade message"),
              template: appstr("view library upgrade template"),
              formatConfig: {
                bold1: { type: "bold" as const }
              },
              replaceDict: {
                bold1: appstr("unlimited number of custom org chart views")
              }
            }
          }
        ]
      : []),
    ...(canAccessAbout
      ? [
          {
            label: "Account details",
            value: "details-account",
            defaultSection: "Account"
          },
          {
            label: "Application details",
            value: "details-application",
            defaultSection: "Application"
          }
        ]
      : [])
  ];

  const BuildOptions = (): SettingMenuOption[] =>
    settingsList.map(settingOption => {
      const base: SettingMenuOption = {
        value: settingOption.value,
        defaultSection: settingOption.defaultSection,
        divider: settingOption.divider,
        dataTestId: `settings-nav-${settingOption.value}`
      };

      if (settingOption.showUpgrade) {
        return {
          ...base,
          content: (
            <Flex justifyContent="space-between" alignItems="center" gap="4px">
              <Text size="small" style={{ color: "var(--txt-disabled)" }}>
                {appstr(settingOption.label)}
              </Text>
              <UpgradePopover
                pillLabel={appstr("Upgrade")}
                pillTestId={`settings-nav-${settingOption.value}-upgrade-pill`}
                // settingsList always pairs showUpgrade with a featureKey — see construction above.
                featureKey={settingOption.featureKey!}
                popoverText={settingOption.upgradePopoverText}
              />
            </Flex>
          )
        };
      }

      return {
        ...base,
        content: <Text size="small">{appstr(settingOption.label)}</Text>,
        clickHandler: () => {
          onClickSettings(settingOption);
          // menu items set to not auto-close on select to faciliate disabled options from closing on click.
          // Manually close the menu after clicking an option
          menuOpenCtrlRef?.current?.setOpen(false);
        }
      };
    });

  const settingOptions = BuildOptions();
  const isWithinSettings =
    location.pathname.startsWith("/settings") || location.pathname.startsWith("/details");

  return { settingOptions, canAccessSettings, isWithinSettings, menuOpenCtrlRef };
};
